import useMediaQuery from '@mui/material/useMediaQuery';

import { TwLoadMenuOptions } from '../components/TwLoadMenu/TwLoadMenu';

import { Theme } from '@mui/material';
import useFeatureFlags from '../providers/FeatureFlags/FeatureFlags.provider';
import useAuth from '../providers/Auth/Auth.provider';
import useNotifications from '../providers/Notifications/Notifications.provider';
import {
  keepLoadDetailOpen,
  openCreateInvoice,
  openEditLoad,
  openInvoicePreview,
  openPdfViewer,
  openSendDocuments,
  openShareLoad,
  openDeleteLoadConfirmation,
  openEditTrip,
  openDeleteTripConfirmation,
  setTripDetailReload,
  openCreateTrip,
  closeLoadDetail,
  setLoadDetailReload,
  openCreateManifestPage
} from '../redux/features/navigation/navigationSlice';
import { useAppDispatch, useAppSelector } from '../redux/hooks';

import {
  setInvoiceDivisionId,
  setInvoiceLoadIds,
  setOrderId,
  setLoadState,
  setSelectedTripId,
  SliceStop,
  setOrdersForConsolidation
} from '../redux/features/orders/ordersSlice';
import { useGetTenantAccountingSettingsQuery } from '../redux/api/tenant/tenantApi';
import {
  ORDER_COVERAGE_STATUS,
  ORDER_STATUS,
  Order,
  ORDER_INVOICE_STATUS,
  OrderSearchFilterParams,
  SearchOrdersResponse,
  PrefillOrderDataResponse
} from '../redux/api/orders/orders.model';
import {
  SearchTripsParams,
  Trip,
  TripSearchResponse,
  TRIP_STATUS,
  TripCommodities
} from '../redux/api/trips/trips.model';
import {
  useLazyPrefillOrderLTLStopsByIdQuery,
  usePatchOrderMutation,
  useUncancelOrderMutation,
  useUpdateOrderStatusMutation
} from '../redux/api/orders/ordersApi';
import { tripApiUtil, usePatchTripMutation } from '../redux/api/trips/tripApi';
import {
  addNewOrderCommoditiesToTrip,
  addOrderCommoditiesToTrip,
  addOrderStopsToTrip,
  updateTripId
} from '../redux/features/trip/tripSlice';
import { formatStopsAndCommodities } from '../pages/CreateTrip/TripOrderInfo/TripOrderInfo';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import RTKUtils from '../utils/rtkUtils';
import { mapPrefilledOrderCommodityToCommodityList } from '../utils/commodity/commodityUtils';

export enum MenuLabels {
  Edit = 'Edit',
  Create_trip = 'Create trip',
  Send_to_driver = 'Send to driver',
  Duplicate = 'Duplicate',
  Email_load_info = 'Email load info',
  Send_invoice = 'Send invoice',
  View_invoice = 'View invoice',
  Save_as_PDF = 'Save as PDF',
  Send_manifest = 'Send manifest',
  Unarchive = 'Unarchive',
  Archive = 'Archive',
  Delete = 'Delete',
  Cancel = 'Cancel',
  Uncancel = 'Un-cancel'
}

const OrderMap = {
  [MenuLabels.Edit]: 1,
  [MenuLabels.Create_trip]: 2,
  [MenuLabels.Send_to_driver]: 2,
  [MenuLabels.Email_load_info]: 3,
  [MenuLabels.Duplicate]: 4,
  [MenuLabels.Save_as_PDF]: 5,
  [MenuLabels.Send_invoice]: 6,
  [MenuLabels.View_invoice]: 7,
  [MenuLabels.Send_manifest]: 7,
  [MenuLabels.Archive]: 8,
  [MenuLabels.Unarchive]: 9,
  [MenuLabels.Cancel]: 10,
  [MenuLabels.Uncancel]: 10,
  [MenuLabels.Delete]: 11
};

function useLoadMenuOptions(
  load: SearchOrdersResponse | Order | null,
  onChangeArchive?: (load: SearchOrdersResponse | Order) => void,
  searchParams?: OrderSearchFilterParams
): TwLoadMenuOptions[] {
  const dispatch = useAppDispatch();
  const { enableSendInvoiceOnMobile, enableHideSendToDriver } = useFeatureFlags();

  // TODO: remove this when all options are available in mobile too.
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { addNotification } = useNotifications();

  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);

  const [patchOrder] = usePatchOrderMutation();
  const [updateOrderStatus] = useUpdateOrderStatusMutation();

  // archive - unarchive load
  const handleChangeArchive = async (id: string, value: boolean): Promise<void> => {
    patchOrder({ id, data: { archived: value } })
      .unwrap()
      .then((response) => {
        if (load) {
          onChangeArchive && onChangeArchive({ ...load, archived: value });
          dispatch(tripApiUtil.invalidateTags(['ActiveTrips']));
          addNotification({
            message: value ? 'Order archived successfully' : 'Order unarchived successfully',
            variant: 'success'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: value ? 'Failed to archive' : 'Failed to unarchive',
          variant: 'error'
        });
      })
      .finally(() => {
        dispatch(setOrderId(undefined));
      });
  };

  const handleCancelOrder = async (id: string): Promise<void> => {
    updateOrderStatus({ id, status: ORDER_STATUS.CANCELLED })
      .unwrap()
      .then((response) => {
        addNotification({
          message: 'Order cancelled successfully',
          variant: 'success'
        });
        if (isLoadDetailOpen) {
          dispatch(setLoadDetailReload(true));
        }
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: error.message || 'Failed to cancel order',
          variant: 'error'
        });
      });
  };

  const sendToDriverOptions = (): TwLoadMenuOptions[] => {
    if (load && !enableHideSendToDriver) {
      // Type guard to check if it's a SearchOrdersResponse or Order
      type LoadTrip = {
        drivers: unknown[];
      };

      if ('trips' in load && Array.isArray(load.trips)) {
        const allDriversEmpty = (load.trips as LoadTrip[]).every(
          (trip) => Array.isArray(trip.drivers) && trip.drivers.length === 0
        );

        return [
          {
            label: MenuLabels.Send_to_driver,
            disabled: allDriversEmpty,
            tooltipText: allDriversEmpty ? `No driver assigned` : '',
            action: (): void => {
              dispatch(setOrderId(load.id));
              dispatch(updateTripId(undefined));
              dispatch(openShareLoad());
            }
          }
        ];
      }
    }
    return [];
  };

  // Add missing actions for existing dialogs
  let options: TwLoadMenuOptions[] = !load
    ? []
    : [
        {
          label: MenuLabels.Edit,
          disabled: !!load.is_locked_by_settlement,
          tooltipText: load.is_locked_by_settlement
            ? `Order is connected to a paid settlement and can't be edited`
            : '',
          action: (): void => {
            dispatch(setOrderId(load.id));
            dispatch(openEditLoad());
          }
        },
        ...sendToDriverOptions(),
        {
          label: MenuLabels.Duplicate,
          action: (): void => {
            // Setup load duplication
            dispatch(setLoadState({ order_id: load.id, duplicate_load: true }));
            dispatch(openEditLoad());
          }
        }
      ];

  if (load && !matchesMobileOrTablet) {
    options.push({
      label: MenuLabels.Email_load_info,
      action: (): void => {
        dispatch(setOrderId(load.id));
        dispatch(openSendDocuments());
      }
    });
  }

  if (
    ((load && 'has_invoice' in load && !load.has_invoice) ||
      (load && 'invoice' in load && !load.invoice)) &&
    (!matchesMobileOrTablet || enableSendInvoiceOnMobile)
  ) {
    options.push({
      label: MenuLabels.Send_invoice,
      action: (): void => {
        if (isLoadDetailOpen) dispatch(keepLoadDetailOpen());
        dispatch(setInvoiceDivisionId(load.division_id || ''));
        dispatch(setInvoiceLoadIds([load.id]));
        dispatch(openCreateInvoice());
      }
    });
  }

  // validation for view invoice
  if (
    (load && 'has_invoice' in load && load.has_invoice) ||
    (load && 'invoice' in load && load.invoice)
  ) {
    options.push({
      label: MenuLabels.View_invoice,
      action: (): void => {
        dispatch(setOrderId(load.id));
        dispatch(openInvoicePreview());
      }
    });
  }

  if (load) {
    const attachmentsCount = (load as SearchOrdersResponse).attachments_count;
    const orderAttachments = (load as Order).order_attachments;

    if (
      (attachmentsCount !== null && attachmentsCount > 0) ||
      (orderAttachments && orderAttachments.length > 0)
    ) {
      options.push({
        label: MenuLabels.Save_as_PDF,
        action: (): void => {
          dispatch(setOrderId(load.id));
          dispatch(openPdfViewer());
        }
      });
    }
  }
  // check archive of load for archive or unarchive options

  if (load && load?.archived) {
    // remove option
    options = options.filter((option) => option.label !== MenuLabels.Archive);
    options.push({
      label: MenuLabels.Unarchive,
      action: (): void => {
        dispatch(setOrderId(load.id));
        handleChangeArchive(load.id, false);
      }
    });
  } else if (load && !load?.archived) {
    options = options.filter((option) => option.label !== MenuLabels.Unarchive);
    options.push({
      label: MenuLabels.Archive,
      action: (): void => {
        dispatch(setOrderId(load.id));
        handleChangeArchive(load.id, true);
      }
    });
  }

  if (load && load.status !== ORDER_STATUS.CANCELLED) {
    options.push({
      label: MenuLabels.Cancel,
      disabled: !!load.is_locked_by_settlement,
      tooltipText: load.is_locked_by_settlement
        ? `Order is connected to a paid settlement and can't be cancelled`
        : '',
      action: (): void => {
        dispatch(setOrderId(load.id));
        handleCancelOrder(load.id);
      }
    });
  }

  // delete must be the last option
  options.push({
    label: MenuLabels.Delete,
    action: (): void => {
      load && dispatch(setOrderId(load.id));
      load && dispatch(openDeleteLoadConfirmation());
    },
    disabled:
      load?.has_invoice ||
      load?.is_locked_by_settlement ||
      load?.invoice_status === ORDER_INVOICE_STATUS.invoicedManually ||
      load?.invoice_status === ORDER_INVOICE_STATUS.paid ||
      false,
    tooltipText:
      load?.has_invoice && load?.is_locked_by_settlement
        ? 'Linked to an invoice and saved settlement'
        : (load?.has_invoice && !load?.is_locked_by_settlement) ||
          load?.invoice_status === ORDER_INVOICE_STATUS.invoicedManually ||
          load?.invoice_status === ORDER_INVOICE_STATUS.paid
        ? 'Linked to an invoice'
        : !load?.has_invoice && load?.is_locked_by_settlement
        ? 'Linked to a saved settlement'
        : ''
  });

  // Ordering menu items
  options.sort(
    (optionA, optionB) =>
      OrderMap[optionA.label as MenuLabels] - OrderMap[optionB.label as MenuLabels]
  );

  return options;
}

// Trip menu for FTL or LTL
function useTripMenuOptions(
  trip: TripSearchResponse | Trip | null,
  orderStatus?: ORDER_STATUS,
  onChangeArchive?: () => void,
  searchParams?: SearchTripsParams
): TwLoadMenuOptions[] {
  const dispatch = useAppDispatch();
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { enableSendInvoiceOnMobile, enableBorderConnect, enableHideSendToDriver, enableCommoditiesDatabase } =
    useFeatureFlags();
  const route = useLocation();

  // TODO: remove this when all options are available in mobile too.
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { addNotification } = useNotifications();

  const filters = useAppSelector((state) => state.activeTrips.filters);
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);

  const hasInvoiceInOrders = trip && (trip as Trip).orders?.some((order) => order.has_invoice);

  const toInvalidate = useMemo(() => {
    return RTKUtils.invalidateTagsForOrdersAndTrips(route.pathname);
  }, [route]);

  const [patchTrip] = usePatchTripMutation();

  const checkFtlHasInvoice = (trip: TripSearchResponse | Trip | null): boolean => {
    let tripHasInvoice = false;
    if (!trip) return tripHasInvoice;

    if (trip.hasOwnProperty('invoice_id')) {
      tripHasInvoice = !!(trip as TripSearchResponse).invoice_id;
    } else if (trip.hasOwnProperty('orders')) {
      tripHasInvoice = !!(trip as Trip).orders?.[0]?.has_invoice;
    }

    return tripHasInvoice;
  };

  // archive - unarchive trip
  const handleChangeArchive = async (id: string, value: boolean): Promise<void> => {
    patchTrip({
      id,
      body: { archived: value },
      tagsToInvalidate: toInvalidate,
      searchTripParams: { filters }
    })
      .unwrap()
      .then((response) => {
        addNotification({
          message: value ? 'Trip archived successfully' : 'Trip unarchived successfully',
          variant: 'success'
        });

        if (route.pathname.includes('trips/board')) {
          dispatch(tripApiUtil.invalidateTags(['ActiveTrips']));
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setTimeout(() => dispatch(setTripDetailReload(true)), 500);

        !enableLtl && dispatch(setSelectedTripId(undefined));
      });
  };

  const handleCancelTrip = async (id: string): Promise<void> => {
    patchTrip({
      id: id,
      body: { status: TRIP_STATUS.CANCELLED },
      tagsToInvalidate: toInvalidate,
      searchTripParams: searchParams
    })
      .unwrap()
      .then((response) => {
        addNotification({
          message: 'Trip cancelled successfully',
          variant: 'success'
        });
        setTimeout(() => dispatch(setTripDetailReload(true)), 500);
        if (route.pathname.includes('trips/board')) {
          dispatch(tripApiUtil.invalidateTags(['ActiveTrips']));
        }
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: error.message || 'Failed to cancel trip',
          variant: 'error'
        });
      });
  };

  const sendToDriverOptions = (): TwLoadMenuOptions[] => {
    if (trip && !enableHideSendToDriver) {
      // see if trip is type SearchTripResponse or Trip
      const isSearchTripResponse = (trip as TripSearchResponse).drivers !== undefined;
      const isTrip = (trip as Trip).drivers !== undefined;
      let allDriversEmpty = false;

      if (isSearchTripResponse) {
        allDriversEmpty = (trip as TripSearchResponse).drivers.length === 0;
      } else if (isTrip) {
        allDriversEmpty = (trip as Trip).drivers.length === 0;
      }

      return [
        {
          label: MenuLabels.Send_to_driver,
          disabled: allDriversEmpty,
          tooltipText: allDriversEmpty ? `No drivers assigned` : '',
          action: (): void => {
            const orderId = (trip as Trip).load_id || (trip as TripSearchResponse).order_id;
            dispatch(updateTripId(trip.id));
            orderId && dispatch(setOrderId(orderId));
            dispatch(openShareLoad());
          }
        }
      ];
    }
    return [];
  };

  const checkDisableTripForFtl = (): boolean => {
    if (!trip) return false;

    let isOrderLockedBySettlement = false;

    if (trip.orders && trip.orders.length > 0) {
      if ((trip as TripSearchResponse).orders?.[0].order) {
        isOrderLockedBySettlement = !!(trip as TripSearchResponse).orders?.[0].order
          ?.is_locked_by_settlement;
      } else {
        isOrderLockedBySettlement = !!(trip as Trip).orders?.[0].is_locked_by_settlement;
      }
    }

    return isOrderLockedBySettlement;
  };

  // Add missing actions for existing dialogs
  let options: TwLoadMenuOptions[] = !trip
    ? []
    : [
        {
          label: MenuLabels.Edit,
          disabled: enableLtl ? trip.is_locked_by_settlement : checkDisableTripForFtl(),
          tooltipText:
            (enableLtl && trip.is_locked_by_settlement) || (!enableLtl && checkDisableTripForFtl())
              ? `Trip is connected to a paid settlement and can't be edited`
              : '',
          action: (): void => {
            if (enableLtl) {
              dispatch(updateTripId(trip.id));
              dispatch(openEditTrip(true));
            } else {
              const orderId =
                (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
                (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
              dispatch(setOrderId(orderId)); // FTL
              dispatch(openEditLoad());
            }
          }
        },
        ...sendToDriverOptions()
      ];
  if (!enableLtl) {
    options.push({
      label: MenuLabels.Duplicate,
      action: (): void => {
        // Setup load duplication
        const orderId =
          (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
          (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById

        dispatch(setLoadState({ order_id: orderId, duplicate_load: true }));
        dispatch(openEditLoad());
      }
    });
  }

  if (trip && !matchesMobileOrTablet && !enableLtl) {
    options.push({
      label: MenuLabels.Email_load_info,
      action: (): void => {
        const orderId =
          (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
          (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
        dispatch(setOrderId(orderId));
        dispatch(openSendDocuments());
      }
    });
  }

  if (
    trip &&
    !enableLtl &&
    !checkFtlHasInvoice(trip) &&
    (!matchesMobileOrTablet || enableSendInvoiceOnMobile)
  ) {
    options.push({
      label: MenuLabels.Send_invoice,
      action: (): void => {
        const orderId =
          (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
          (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById

        const divisionId =
          (trip as TripSearchResponse).orders?.[0].order?.division_id ||
          (trip as Trip).orders?.[0].division_id;

        if (orderId === undefined) {
          addNotification({
            message: 'Failed to send invoice, load id is missing',
            variant: 'error'
          });
          return;
        }

        if (divisionId === undefined || divisionId === null) {
          addNotification({
            message: 'Failed to send invoice, division id is missing',
            variant: 'error'
          });
          return;
        }

        if (isLoadDetailOpen) dispatch(keepLoadDetailOpen());
        dispatch(setInvoiceDivisionId(divisionId));
        dispatch(setInvoiceLoadIds([orderId]));

        dispatch(openCreateInvoice());
      }
    });
  }

  // validation for view invoice
  if (!enableLtl && checkFtlHasInvoice(trip)) {
    options.push({
      label: MenuLabels.View_invoice,
      action: (): void => {
        const orderId =
          (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
          (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
        dispatch(setOrderId(orderId));
        dispatch(openInvoicePreview());
      }
    });
  }

  trip &&
    (trip as TripSearchResponse).order_attachments_count > 0 &&
    !enableLtl &&
    options.push({
      label: MenuLabels.Save_as_PDF,
      action: (): void => {
        const orderId =
          (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
          (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
        dispatch(setOrderId(orderId));
        dispatch(openPdfViewer());
      }
    });

  if (enableBorderConnect && trip) {
    options.push({
      label: MenuLabels.Send_manifest,
      action: (): void => {
        dispatch(updateTripId(trip.id));
        dispatch(openCreateManifestPage(true));
      }
    });
  }

  if ((trip && (trip as TripSearchResponse)?.order_is_archived && !enableLtl) || trip?.archived) {
    // remove option
    options = options.filter((option) => option.label !== MenuLabels.Archive);
    options.push({
      label: MenuLabels.Unarchive,
      action: (): void => {
        if (!enableLtl) {
          const orderId =
            (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
            (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
          dispatch(setOrderId(orderId));
        }
        handleChangeArchive((trip as TripSearchResponse).id, false);
      }
    });
  } else if (
    (trip && !((trip as TripSearchResponse)?.order_is_archived && !enableLtl)) ||
    !trip?.archived
  ) {
    options = options.filter((option) => option.label !== MenuLabels.Unarchive);
    options.push({
      label: MenuLabels.Archive,
      action: (): void => {
        if (!enableLtl) {
          const orderId =
            (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
            (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
          dispatch(setOrderId(orderId));
        }
        handleChangeArchive((trip as TripSearchResponse).id, true);
      }
    });
  }

  if (
    (trip && orderStatus !== ORDER_STATUS.CANCELLED && !enableLtl) ||
    (trip && trip.status !== TRIP_STATUS.CANCELLED && enableLtl)
  ) {
    options.push({
      label: MenuLabels.Cancel,
      disabled: enableLtl ? trip.is_locked_by_settlement : checkDisableTripForFtl(),
      tooltipText:
        (enableLtl && trip.is_locked_by_settlement) || (!enableLtl && checkDisableTripForFtl())
          ? `Trip is connected to a paid settlement and can't be cancelled`
          : '',
      action: (): void => {
        if (!enableLtl) {
          const orderId =
            (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
            (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
          dispatch(setOrderId(orderId));
        }
        handleCancelTrip(trip.id);
      }
    });
  }

  // delete must be the last option
  options.push({
    label: MenuLabels.Delete,
    action: (): void => {
      if (!enableLtl) {
        const orderId =
          (trip as TripSearchResponse).orders?.[0].order_id || // this comes from search
          (trip as TripSearchResponse).orders?.[0].id; // this comes from tripById
        trip && dispatch(setOrderId(orderId));
        trip && dispatch(openDeleteLoadConfirmation());
      } else {
        trip && dispatch(updateTripId(trip.id));
        trip && dispatch(openDeleteTripConfirmation());
      }
    },
    disabled: !trip
      ? false
      : !!(
          (trip as TripSearchResponse)?.invoice_id ||
          hasInvoiceInOrders ||
          (trip as TripSearchResponse)?.is_locked_by_settlement
        ),
    tooltipText:
      ((trip as TripSearchResponse)?.invoice_id || hasInvoiceInOrders) &&
      (trip as TripSearchResponse)?.is_locked_by_settlement
        ? 'Linked to an invoice and saved settlement'
        : ((trip as TripSearchResponse)?.invoice_id || hasInvoiceInOrders) &&
          !(trip as TripSearchResponse)?.is_locked_by_settlement
        ? 'Linked to an invoice'
        : (!(trip as TripSearchResponse)?.invoice_id || !hasInvoiceInOrders) &&
          (trip as TripSearchResponse)?.is_locked_by_settlement
        ? 'Linked to a saved settlement'
        : ''
  });

  // Ordering menu items
  options.sort(
    (optionA, optionB) =>
      OrderMap[optionA.label as MenuLabels] - OrderMap[optionB.label as MenuLabels]
  );

  return options;
}

function useLTLOrdersMenuOptions(
  order: SearchOrdersResponse | Order | null,
  onChangeArchive?: (load: SearchOrdersResponse | Order) => void
): TwLoadMenuOptions[] {
  const dispatch = useAppDispatch();
  const { enableSendInvoiceOnMobile, enableCommoditiesDatabase } = useFeatureFlags();

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { addNotification } = useNotifications();
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const isTripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);
  const [updateOrderStatus] = useUpdateOrderStatusMutation();
  const [prefillOrderStops] = useLazyPrefillOrderLTLStopsByIdQuery();
  const [uncancelOrder] = useUncancelOrderMutation();

  const handleCancelOrder = async (id: string): Promise<void> => {
    updateOrderStatus({ id, status: ORDER_STATUS.CANCELLED })
      .unwrap()
      .then((response) => {
        addNotification({
          message: 'Order cancelled successfully',
          variant: 'success'
        });
        if (isLoadDetailOpen) {
          dispatch(setLoadDetailReload(true));
        }
        if (isTripDetailOpen) {
          dispatch(setTripDetailReload(true));
        }
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: error.message || 'Failed to cancel order',
          variant: 'error'
        });
      });
  };

  const handleUnCancelOrder = async (id: string): Promise<void> => {
    uncancelOrder(id)
      .unwrap()
      .then((response) => {
        addNotification({
          message: 'Order un-cancelled successfully',
          variant: 'success'
        });
        if (isTripDetailOpen) {
          dispatch(setTripDetailReload(true));
        }
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          message: error.message || 'Failed to un-cancel order',
          variant: 'error'
        });
      });
  };

  const createTripFromOrder = (): void => {
    if (!order) return;

    const allStops: SliceStop[] = [];
    const allCommodities: TripCommodities[] = [];
    prefillOrderStops(order.id)
      .unwrap()
      .then((result: PrefillOrderDataResponse) => {
        const orderData = {
          id: result.id,
          readable_id: result.readable_id,
          customer_number: result.customer_order_number || '',
          customerName: result.raw_customer?.name || ''
        };
        const { stops, commodities } = formatStopsAndCommodities(
          result.stops,
          result.order_commodities,
          orderData
        );

        allStops.push(...stops);
        allCommodities.push(...commodities);

        if (isLoadDetailOpen) {
          dispatch(closeLoadDetail());
        }

        if (enableCommoditiesDatabase) {
          const tripCommodities = commodities.map((commodity) => {
            return mapPrefilledOrderCommodityToCommodityList(commodity);
          });
          dispatch(addNewOrderCommoditiesToTrip(tripCommodities));
        } else {
          dispatch(addOrderCommoditiesToTrip(allCommodities));
        }

        dispatch(setOrdersForConsolidation([{ id: result.id, readable_id: result.readable_id }]));
        dispatch(addOrderStopsToTrip(allStops));
        dispatch(openCreateTrip(true));
      })
      .catch((error) => {
        console.error(error);
        addNotification({
          variant: 'error',
          message: 'Error while getting data from Order for Trip creation, please try again'
        });
      });
  };

  // Add missing actions for existing dialogs
  const options: TwLoadMenuOptions[] = !order
    ? []
    : [
        {
          label: MenuLabels.Edit,
          disabled: !!order.is_locked_by_settlement,
          tooltipText: order.is_locked_by_settlement
            ? `Order is connected to a paid settlement and can't be edited`
            : '',
          action: (): void => {
            dispatch(setOrderId(order.id));
            dispatch(openEditLoad());
          }
        },

        {
          label: MenuLabels.Duplicate,
          action: (): void => {
            // Setup load duplication
            dispatch(setLoadState({ order_id: order.id, duplicate_load: true }));
            dispatch(openEditLoad());
          }
        }
      ];

  if (order && order.coverage !== ORDER_COVERAGE_STATUS.COVERED) {
    options.push({
      label: MenuLabels.Create_trip,
      action: (): void => {
        createTripFromOrder();
      }
    });
  }

  if (order && !matchesMobileOrTablet) {
    options.push({
      label: MenuLabels.Email_load_info,
      action: (): void => {
        dispatch(setOrderId(order.id));
        dispatch(openSendDocuments());
      }
    });
  }

  // validation for enable quickbooks or truckbase invoice
  if (
    ((order && 'has_invoice' in order && !order.has_invoice) ||
      (order && 'invoice' in order && !order.invoice)) &&
    (!matchesMobileOrTablet || enableSendInvoiceOnMobile)
  ) {
    options.push({
      label: MenuLabels.Send_invoice,
      action: (): void => {
        if (isLoadDetailOpen) dispatch(keepLoadDetailOpen());
        dispatch(setInvoiceDivisionId(order.division_id || ''));
        dispatch(setInvoiceLoadIds([order.id]));
        dispatch(openCreateInvoice());
      }
    });
  }

  // validation for view invoice
  if (
    (order && 'has_invoice' in order && order.has_invoice) ||
    (order && 'invoice' in order && order.invoice)
  ) {
    options.push({
      label: MenuLabels.View_invoice,
      action: (): void => {
        dispatch(setOrderId(order.id));
        dispatch(openInvoicePreview());
      }
    });
  }

  order &&
    ((order as SearchOrdersResponse).attachments_count > 0 ||
      (order as Order).order_attachments?.length > 0) &&
    options.push({
      label: MenuLabels.Save_as_PDF,
      action: (): void => {
        dispatch(setOrderId(order.id));
        dispatch(openPdfViewer());
      }
    });

  // cancel order
  if (order && order.status !== ORDER_STATUS.CANCELLED) {
    options.push({
      label: MenuLabels.Cancel,
      disabled: !!order.is_locked_by_settlement,
      tooltipText: !!order.is_locked_by_settlement
        ? `Order is connected to a paid settlement and can't be cancelled`
        : '',
      action: (): void => {
        dispatch(setOrderId(order.id));
        handleCancelOrder(order.id);
      }
    });
  }

  // uncancel order
  if (order && order.status === ORDER_STATUS.CANCELLED) {
    options.push({
      label: MenuLabels.Uncancel,
      action: (): void => {
        dispatch(setOrderId(order.id));
        handleUnCancelOrder(order.id);
      }
    });
  }

  // delete must be the last option
  options.push({
    label: MenuLabels.Delete,
    action: (): void => {
      order && dispatch(setOrderId(order.id));
      order && dispatch(openDeleteLoadConfirmation());
    },
    disabled:
      order?.has_invoice ||
      order?.is_locked_by_settlement ||
      order?.coverage !== ORDER_COVERAGE_STATUS.UNCOVERED ||
      false,
    tooltipText:
      order?.has_invoice && order?.is_locked_by_settlement
        ? 'Linked to an invoice and saved settlement'
        : order?.has_invoice && !order?.is_locked_by_settlement
        ? 'Linked to an invoice'
        : !order?.has_invoice && order?.is_locked_by_settlement
        ? 'Linked to a saved settlement'
        : order?.coverage !== ORDER_COVERAGE_STATUS.UNCOVERED
        ? 'Order has to be fully Uncovered to delete it'
        : ''
  });

  // Ordering menu items
  options.sort(
    (optionA, optionB) =>
      OrderMap[optionA.label as MenuLabels] - OrderMap[optionB.label as MenuLabels]
  );

  return options;
}

export { useLoadMenuOptions, useTripMenuOptions, useLTLOrdersMenuOptions };
